import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import type { InventoryFieldDefinitionDto, InventoryFieldType } from './contractsMirror.js';
import {
  useCreateInventoryCategoryMutation,
  useCreateInventoryFieldMutation,
  useDeleteInventoryFieldMutation,
  useEditInventoryCategoryMutation,
  useEditInventoryFieldMutation,
  useInventoryQuery,
} from './useInventoryQuery.js';

const INLINE_INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const INLINE_TEXTAREA_CLASS =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** Parse a field definition's stored options JSON back to a newline-joined editable string. */
function fieldOptionsToText(def: InventoryFieldDefinitionDto): string {
  if (!def.options) return '';
  try {
    const parsed = JSON.parse(def.options) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter((o): o is string => typeof o === 'string').join('\n');
    }
  } catch {
    // Fall through to the raw value.
  }
  return def.options;
}

/**
 * The category management view (UI-SPEC: "category management as a manage view"). Lists every
 * category with its per-category counts AND its custom field definitions (key/label/type/unit) —
 * the load-bearing custom-per-category field model, surfaced faithfully (D-03). The coarse
 * inventory query already carries the categories + their `fieldDefinitions`, so this view reuses
 * it (no extra round-trip).
 *
 * Creating/editing categories + fields still opens the legacy SSR forms (the api-host has no
 * category/field WRITE form contract beyond the coarse mutations this plan added); the "+ Add"
 * affordances are present and the manage view preserves the full model + every action surface
 * (D-03). Token-only (no hex, BRAND-06); a semantic structure for A11Y-01.
 */

/** Human label for a field type (i18n-keyed). */
function useFieldTypeLabel() {
  const { t } = useTranslation();
  return (type: InventoryFieldType): string => {
    switch (type) {
      case 'number':
        return t('inventory:categories.fieldType.number');
      case 'textarea':
        return t('inventory:categories.fieldType.textarea');
      case 'select':
        return t('inventory:categories.fieldType.select');
      default:
        return t('inventory:categories.fieldType.text');
    }
  };
}

export function CategoryManageView() {
  const { t } = useTranslation();
  const fieldTypeLabel = useFieldTypeLabel();
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [activeFieldCategoryId, setActiveFieldCategoryId] = useState<number | null>(null);
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldType, setFieldType] = useState<InventoryFieldType>('text');
  const [fieldUnit, setFieldUnit] = useState('');
  const [fieldRequired, setFieldRequired] = useState(false);
  const [fieldOptionsText, setFieldOptionsText] = useState('');
  const [showFieldOptionsError, setShowFieldOptionsError] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Category-edit inline form state (one category open at a time).
  const [editCategoryId, setEditCategoryId] = useState<number | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryDescription, setEditCategoryDescription] = useState('');

  // Field-edit inline form state (one field open at a time).
  const [editFieldId, setEditFieldId] = useState<number | null>(null);
  const [editFieldLabel, setEditFieldLabel] = useState('');
  const [editFieldType, setEditFieldType] = useState<InventoryFieldType>('text');
  const [editFieldUnit, setEditFieldUnit] = useState('');
  const [editFieldRequired, setEditFieldRequired] = useState(false);
  const [editFieldOptionsText, setEditFieldOptionsText] = useState('');
  const [showEditFieldOptionsError, setShowEditFieldOptionsError] = useState(false);

  // Field-delete confirmation.
  const [deletingField, setDeletingField] = useState<InventoryFieldDefinitionDto | null>(null);

  const createCategoryMutation = useCreateInventoryCategoryMutation();
  const createFieldMutation = useCreateInventoryFieldMutation();
  const editCategoryMutation = useEditInventoryCategoryMutation();
  const editFieldMutation = useEditInventoryFieldMutation();
  const deleteFieldMutation = useDeleteInventoryFieldMutation();

  // Include archived so every category is manageable from here.
  const query = useInventoryQuery({ archived: true });

  function openCategoryEdit(category: { id: number; name: string; description: string | null }) {
    setStatusMessage(null);
    setEditFieldId(null);
    setEditCategoryId(category.id);
    setEditCategoryName(category.name);
    setEditCategoryDescription(category.description ?? '');
    editCategoryMutation.reset();
  }

  function closeCategoryEdit() {
    setEditCategoryId(null);
    editCategoryMutation.reset();
  }

  function handleCategoryEditSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editCategoryId) return;
    setStatusMessage(null);
    editCategoryMutation.mutate(
      {
        categoryId: editCategoryId,
        name: editCategoryName.trim(),
        description: editCategoryDescription.trim() || undefined,
      },
      {
        onSuccess: () => {
          closeCategoryEdit();
          setStatusMessage(t('inventory:categories.form.updateSuccess'));
        },
      },
    );
  }

  function openFieldEdit(def: InventoryFieldDefinitionDto) {
    setStatusMessage(null);
    setEditCategoryId(null);
    setEditFieldId(def.id);
    setEditFieldLabel(def.label);
    setEditFieldType(def.fieldType);
    setEditFieldUnit(def.unit ?? '');
    setEditFieldRequired(def.required);
    setEditFieldOptionsText(fieldOptionsToText(def));
    setShowEditFieldOptionsError(false);
    editFieldMutation.reset();
  }

  function closeFieldEdit() {
    setEditFieldId(null);
    setShowEditFieldOptionsError(false);
    editFieldMutation.reset();
  }

  function handleFieldEditSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editFieldId) return;
    setStatusMessage(null);

    const options = editFieldOptionsText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (editFieldType === 'select' && options.length === 0) {
      setShowEditFieldOptionsError(true);
      return;
    }
    setShowEditFieldOptionsError(false);

    editFieldMutation.mutate(
      {
        fieldId: editFieldId,
        label: editFieldLabel.trim(),
        fieldType: editFieldType,
        unit: editFieldUnit.trim() || undefined,
        required: editFieldRequired,
        options: editFieldType === 'select' ? options : undefined,
      },
      {
        onSuccess: () => {
          closeFieldEdit();
          setStatusMessage(t('inventory:categories.form.fieldUpdateSuccess'));
        },
      },
    );
  }

  function confirmDeleteField() {
    if (!deletingField) return;
    const fieldId = deletingField.id;
    setDeletingField(null);
    setStatusMessage(null);
    deleteFieldMutation.mutate(
      { fieldId },
      {
        onSuccess: () => {
          setStatusMessage(t('inventory:categories.form.fieldDeleteSuccess'));
        },
      },
    );
  }

  function resetCategoryForm() {
    setCategoryName('');
    setCategoryDescription('');
    setShowCategoryForm(false);
    createCategoryMutation.reset();
  }

  function resetFieldForm() {
    setFieldLabel('');
    setFieldType('text');
    setFieldUnit('');
    setFieldRequired(false);
    setFieldOptionsText('');
    setShowFieldOptionsError(false);
    createFieldMutation.reset();
  }

  function handleCategorySubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatusMessage(null);
    createCategoryMutation.mutate(
      {
        name: categoryName.trim(),
        description: categoryDescription.trim() || undefined,
      },
      {
        onSuccess: () => {
          resetCategoryForm();
          setStatusMessage(t('inventory:categories.form.createSuccess'));
        },
      },
    );
  }

  function handleFieldSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatusMessage(null);
    if (!activeFieldCategoryId) return;

    const options = fieldOptionsText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (fieldType === 'select' && options.length === 0) {
      setShowFieldOptionsError(true);
      return;
    }
    setShowFieldOptionsError(false);

    createFieldMutation.mutate(
      {
        categoryId: activeFieldCategoryId,
        label: fieldLabel.trim(),
        fieldType,
        unit: fieldUnit.trim() || undefined,
        required: fieldRequired,
        options: fieldType === 'select' ? options : undefined,
      },
      {
        onSuccess: () => {
          setActiveFieldCategoryId(null);
          resetFieldForm();
          setStatusMessage(t('inventory:categories.form.fieldCreateSuccess'));
        },
      },
    );
  }

  function toggleFieldForm(categoryId: number) {
    setStatusMessage(null);
    setShowCategoryForm(false);
    createCategoryMutation.reset();
    if (activeFieldCategoryId === categoryId) {
      setActiveFieldCategoryId(null);
      resetFieldForm();
      return;
    }
    setActiveFieldCategoryId(categoryId);
    resetFieldForm();
  }

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('inventory:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('inventory:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('inventory:error.retry')}
        </button>
      </section>
    );
  }

  const categories = query.data.categories ?? [];

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('inventory:categories.title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {t('inventory:categories.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="secondary">
            <Link to="/inventory">{t('inventory:actions.backToItems')}</Link>
          </Button>
          <Button
            onClick={() => {
              setStatusMessage(null);
              setActiveFieldCategoryId(null);
              resetFieldForm();
              setShowCategoryForm((open) => !open);
              createCategoryMutation.reset();
            }}
          >
            {showCategoryForm
              ? t('inventory:categories.form.cancel')
              : t('inventory:categories.addCategory')}
          </Button>
        </div>
      </header>

      {statusMessage ? (
        <p role="status" className="mb-4 text-secondary-foreground text-sm">
          {statusMessage}
        </p>
      ) : null}

      {showCategoryForm ? (
        <form
          onSubmit={handleCategorySubmit}
          className="mb-6 max-w-2xl rounded-md border border-border bg-card p-6"
        >
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('inventory:categories.form.addCategoryHeading')}
          </h2>
          <div className="grid gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="new-category-name" className="font-medium text-foreground text-sm">
                {t('inventory:categories.form.name')}
              </label>
              <input
                id="new-category-name"
                value={categoryName}
                onChange={(event) => setCategoryName(event.target.value)}
                maxLength={120}
                required
                className="h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="new-category-description"
                className="font-medium text-foreground text-sm"
              >
                {t('inventory:categories.form.description')}
              </label>
              <textarea
                id="new-category-description"
                value={categoryDescription}
                onChange={(event) => setCategoryDescription(event.target.value)}
                maxLength={500}
                rows={4}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </div>
          </div>
          {createCategoryMutation.isError ? (
            <p role="alert" className="mt-3 text-destructive text-sm">
              {t('inventory:categories.form.createError')}
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={createCategoryMutation.isPending}>
              {t('inventory:categories.form.submitCategory')}
            </Button>
            <Button type="button" variant="secondary" onClick={resetCategoryForm}>
              {t('inventory:categories.form.cancel')}
            </Button>
          </div>
        </form>
      ) : null}

      {categories.length === 0 ? (
        <div className="rounded-md border border-border border-dashed bg-card p-12 text-center">
          <h2 className="font-display font-semibold text-foreground text-lg">
            {t('inventory:empty.categoriesHeading')}
          </h2>
          <p className="mt-1 text-muted-foreground text-sm">
            {t('inventory:empty.categoriesBody')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {categories.map((category) => (
            <section
              key={category.id}
              className="rounded-md border border-border bg-card p-6"
              aria-labelledby={`category-${category.id}-name`}
            >
              {activeFieldCategoryId === category.id ? (
                <form
                  onSubmit={handleFieldSubmit}
                  className="mb-4 rounded-md border border-border/60 p-4"
                >
                  <h3 className="mb-3 font-medium text-foreground text-sm">
                    {t('inventory:categories.form.addFieldHeading', { category: category.name })}
                  </h3>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1 sm:col-span-2">
                      <label
                        htmlFor={`new-field-label-${category.id}`}
                        className="font-medium text-foreground text-sm"
                      >
                        {t('inventory:categories.form.label')}
                      </label>
                      <input
                        id={`new-field-label-${category.id}`}
                        value={fieldLabel}
                        onChange={(event) => setFieldLabel(event.target.value)}
                        maxLength={120}
                        required
                        className="h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor={`new-field-type-${category.id}`}
                        className="font-medium text-foreground text-sm"
                      >
                        {t('inventory:categories.form.fieldType')}
                      </label>
                      <select
                        id={`new-field-type-${category.id}`}
                        value={fieldType}
                        onChange={(event) => {
                          setFieldType(event.target.value as InventoryFieldType);
                          setShowFieldOptionsError(false);
                        }}
                        className="h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      >
                        <option value="text">{t('inventory:categories.fieldType.text')}</option>
                        <option value="number">{t('inventory:categories.fieldType.number')}</option>
                        <option value="textarea">
                          {t('inventory:categories.fieldType.textarea')}
                        </option>
                        <option value="select">{t('inventory:categories.fieldType.select')}</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor={`new-field-unit-${category.id}`}
                        className="font-medium text-foreground text-sm"
                      >
                        {t('inventory:categories.form.unit')}
                      </label>
                      <input
                        id={`new-field-unit-${category.id}`}
                        value={fieldUnit}
                        onChange={(event) => setFieldUnit(event.target.value)}
                        maxLength={60}
                        className="h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="inline-flex items-center gap-2 font-medium text-foreground text-sm">
                        <input
                          type="checkbox"
                          checked={fieldRequired}
                          onChange={(event) => setFieldRequired(event.target.checked)}
                          className="size-4 rounded border border-border bg-background text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        />
                        {t('inventory:categories.form.required')}
                      </label>
                    </div>

                    {fieldType === 'select' ? (
                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`new-field-options-${category.id}`}
                          className="mb-1 block font-medium text-foreground text-sm"
                        >
                          {t('inventory:categories.form.options')}
                        </label>
                        <textarea
                          id={`new-field-options-${category.id}`}
                          value={fieldOptionsText}
                          onChange={(event) => setFieldOptionsText(event.target.value)}
                          rows={4}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        />
                        <p className="mt-1 text-muted-foreground text-xs">
                          {t('inventory:categories.form.optionsHelp')}
                        </p>
                      </div>
                    ) : null}
                  </div>

                  {showFieldOptionsError ? (
                    <p role="alert" className="mt-3 text-destructive text-sm">
                      {t('inventory:categories.form.optionsRequired')}
                    </p>
                  ) : null}
                  {createFieldMutation.isError ? (
                    <p role="alert" className="mt-3 text-destructive text-sm">
                      {t('inventory:categories.form.fieldCreateError')}
                    </p>
                  ) : null}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button type="submit" disabled={createFieldMutation.isPending}>
                      {t('inventory:categories.form.submitField')}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setActiveFieldCategoryId(null);
                        resetFieldForm();
                      }}
                    >
                      {t('inventory:categories.form.cancel')}
                    </Button>
                  </div>
                </form>
              ) : null}

              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h2
                    id={`category-${category.id}-name`}
                    className="font-display font-semibold text-foreground text-lg"
                  >
                    {category.name}
                  </h2>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    {t('inventory:categories.itemCount', { count: category.itemCount })} ·{' '}
                    {t('inventory:categories.fieldCount', { count: category.fieldCount })}
                  </p>
                </div>
                {/* The h2 beside this already prints the category name. It stays on the accessible
                    name, not in the visible label. Cancel needs no such label — it is unambiguous
                    once the editor beneath it is open. */}
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={
                    editCategoryId === category.id
                      ? undefined
                      : t('inventory:categories.editCategory', { name: category.name })
                  }
                  onClick={() =>
                    editCategoryId === category.id
                      ? closeCategoryEdit()
                      : openCategoryEdit(category)
                  }
                >
                  {editCategoryId === category.id
                    ? t('inventory:categories.form.cancel')
                    : t('inventory:categories.edit')}
                </Button>
              </div>

              {editCategoryId === category.id ? (
                <form
                  onSubmit={handleCategoryEditSubmit}
                  className="mt-3 rounded-md border border-border/60 p-4"
                >
                  <h3 className="mb-3 font-medium text-foreground text-sm">
                    {t('inventory:categories.form.editCategoryHeading', {
                      category: category.name,
                    })}
                  </h3>
                  <div className="grid gap-3">
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor={`edit-category-name-${category.id}`}
                        className="font-medium text-foreground text-sm"
                      >
                        {t('inventory:categories.form.name')}
                      </label>
                      <input
                        id={`edit-category-name-${category.id}`}
                        value={editCategoryName}
                        onChange={(event) => setEditCategoryName(event.target.value)}
                        maxLength={120}
                        required
                        className={INLINE_INPUT_CLASS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor={`edit-category-description-${category.id}`}
                        className="font-medium text-foreground text-sm"
                      >
                        {t('inventory:categories.form.description')}
                      </label>
                      <textarea
                        id={`edit-category-description-${category.id}`}
                        value={editCategoryDescription}
                        onChange={(event) => setEditCategoryDescription(event.target.value)}
                        maxLength={500}
                        rows={3}
                        className={INLINE_TEXTAREA_CLASS}
                      />
                    </div>
                  </div>
                  {editCategoryMutation.isError ? (
                    <p role="alert" className="mt-3 text-destructive text-sm">
                      {t('inventory:categories.form.updateError')}
                    </p>
                  ) : null}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button type="submit" disabled={editCategoryMutation.isPending}>
                      {t('inventory:categories.form.submitCategoryEdit')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={closeCategoryEdit}>
                      {t('inventory:categories.form.cancel')}
                    </Button>
                  </div>
                </form>
              ) : null}

              <h3 className="mt-4 mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                {t('inventory:categories.fieldsHeading')}
              </h3>
              {category.fieldDefinitions.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t('inventory:categories.noFields')}
                </p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {category.fieldDefinitions.map((def) => (
                    <li key={def.id} className="rounded-md border border-border/60 px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium text-foreground text-sm">
                          {def.label}
                          {def.unit ? (
                            <span className="ml-1 text-muted-foreground text-xs">({def.unit})</span>
                          ) : null}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground text-xs">
                            {fieldTypeLabel(def.fieldType)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              editFieldId === def.id ? closeFieldEdit() : openFieldEdit(def)
                            }
                          >
                            {editFieldId === def.id
                              ? t('inventory:categories.form.cancel')
                              : t('inventory:categories.editField', { label: def.label })}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={deleteFieldMutation.isPending}
                            onClick={() => setDeletingField(def)}
                          >
                            {t('inventory:categories.deleteField', { label: def.label })}
                          </Button>
                        </div>
                      </div>

                      {editFieldId === def.id ? (
                        <form onSubmit={handleFieldEditSubmit} className="mt-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div className="flex flex-col gap-1 sm:col-span-2">
                              <label
                                htmlFor={`edit-field-label-${def.id}`}
                                className="font-medium text-foreground text-sm"
                              >
                                {t('inventory:categories.form.label')}
                              </label>
                              <input
                                id={`edit-field-label-${def.id}`}
                                value={editFieldLabel}
                                onChange={(event) => setEditFieldLabel(event.target.value)}
                                maxLength={120}
                                required
                                className={INLINE_INPUT_CLASS}
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label
                                htmlFor={`edit-field-type-${def.id}`}
                                className="font-medium text-foreground text-sm"
                              >
                                {t('inventory:categories.form.fieldType')}
                              </label>
                              <select
                                id={`edit-field-type-${def.id}`}
                                value={editFieldType}
                                onChange={(event) => {
                                  setEditFieldType(event.target.value as InventoryFieldType);
                                  setShowEditFieldOptionsError(false);
                                }}
                                className={INLINE_INPUT_CLASS}
                              >
                                <option value="text">
                                  {t('inventory:categories.fieldType.text')}
                                </option>
                                <option value="number">
                                  {t('inventory:categories.fieldType.number')}
                                </option>
                                <option value="textarea">
                                  {t('inventory:categories.fieldType.textarea')}
                                </option>
                                <option value="select">
                                  {t('inventory:categories.fieldType.select')}
                                </option>
                              </select>
                            </div>
                            <div className="flex flex-col gap-1">
                              <label
                                htmlFor={`edit-field-unit-${def.id}`}
                                className="font-medium text-foreground text-sm"
                              >
                                {t('inventory:categories.form.unit')}
                              </label>
                              <input
                                id={`edit-field-unit-${def.id}`}
                                value={editFieldUnit}
                                onChange={(event) => setEditFieldUnit(event.target.value)}
                                maxLength={60}
                                className={INLINE_INPUT_CLASS}
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="inline-flex items-center gap-2 font-medium text-foreground text-sm">
                                <input
                                  type="checkbox"
                                  checked={editFieldRequired}
                                  onChange={(event) => setEditFieldRequired(event.target.checked)}
                                  className="size-4 rounded border border-border bg-background text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                                />
                                {t('inventory:categories.form.required')}
                              </label>
                            </div>
                            {editFieldType === 'select' ? (
                              <div className="sm:col-span-2">
                                <label
                                  htmlFor={`edit-field-options-${def.id}`}
                                  className="mb-1 block font-medium text-foreground text-sm"
                                >
                                  {t('inventory:categories.form.options')}
                                </label>
                                <textarea
                                  id={`edit-field-options-${def.id}`}
                                  value={editFieldOptionsText}
                                  onChange={(event) => setEditFieldOptionsText(event.target.value)}
                                  rows={4}
                                  className={INLINE_TEXTAREA_CLASS}
                                />
                                <p className="mt-1 text-muted-foreground text-xs">
                                  {t('inventory:categories.form.optionsHelp')}
                                </p>
                              </div>
                            ) : null}
                          </div>
                          {showEditFieldOptionsError ? (
                            <p role="alert" className="mt-3 text-destructive text-sm">
                              {t('inventory:categories.form.optionsRequired')}
                            </p>
                          ) : null}
                          {editFieldMutation.isError ? (
                            <p role="alert" className="mt-3 text-destructive text-sm">
                              {t('inventory:categories.form.fieldUpdateError')}
                            </p>
                          ) : null}
                          <div className="mt-4 flex flex-wrap items-center gap-2">
                            <Button type="submit" disabled={editFieldMutation.isPending}>
                              {t('inventory:categories.form.submitFieldEdit')}
                            </Button>
                            <Button type="button" variant="secondary" onClick={closeFieldEdit}>
                              {t('inventory:categories.form.cancel')}
                            </Button>
                          </div>
                        </form>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleFieldForm(category.id)}
                  disabled={createFieldMutation.isPending && activeFieldCategoryId !== category.id}
                >
                  {activeFieldCategoryId === category.id
                    ? t('inventory:categories.form.cancel')
                    : t('inventory:categories.addField')}
                </Button>
              </div>
            </section>
          ))}
        </div>
      )}

      <p className="mt-6 text-muted-foreground text-xs">{t('inventory:categories.manageHint')}</p>

      <ConfirmDialog
        open={deletingField !== null}
        heading={t('inventory:categories.deleteFieldDialog.heading')}
        body={
          deletingField
            ? t('inventory:categories.deleteFieldDialog.body', { label: deletingField.label })
            : t('inventory:categories.deleteFieldDialog.body', { label: '' })
        }
        confirmLabel={t('inventory:categories.deleteFieldDialog.confirm')}
        cancelLabel={t('inventory:categories.deleteFieldDialog.cancel')}
        confirmPending={deleteFieldMutation.isPending}
        onCancel={() => setDeletingField(null)}
        onConfirm={confirmDeleteField}
      />
    </div>
  );
}
